import React from 'react';
import router from 'next/router';

import { withAuth } from '@/components/with_auth';
import routes from '@/services/routes';
import IconWrapper from '@/components/commons/IconWrapper';

import LandingWithCards from '@/components/layout/LandingWithCards';
import QuestionCircleOutline from '@/components/commons/icons/QuestionCircleOutline';
import GlobeOutline from '@/components/commons/icons/GlobeOutline';
import FolderOutline from '@/components/commons/icons/FolderOutline';
import ClipboardCheckOutline from '@/components/commons/icons/ClipboardCheckOutline';

const mainTitle = {
    title: 'Inbox',
    subtitle: 'Eliminate the noise and focus your inbox on what\'s really important.',
};

const bigNavCards = [
    {
        key: 'inbox-assistant',
        heading: {
            children: 'What is Sixty\'s inbox assistant?',
            bold: true,
        },
        body: {
            text: 'Focus your inbox on what\'s most important. Our inbox assistant learns your priorities and automatically manages your incoming mail so only the messages that need your attention land in your inbox. Everything else is set aside so you can deal with it later—or never.',
            style: 'my-5',
        },
        link: {
            children: (
                <div className="text-sm text-progressBarBlue font-medium underline flex items-center">
                    <IconWrapper
                        svg={<QuestionCircleOutline className="w-5 h-5 text-cleanSlate" />}
                        className="mr-1"
                    />
                    Get help with Inbox features
                </div>
            ),
            url: process.env.NEXT_PUBLIC_HELPSCOUT_ROOT,
        },
        target: '_blank',
        image: {
            src: '/images/inbox/woman-next-to-large-envelope.png',
            alt: 'Woman next to large envelope',
            style: 'w-auto max-h-[120px] max-w-[200px] sm:max-h-[200px]',
        },
        externalStyles: 'm-2 flexbox gap-4 md:mx-5 md:my-6',
        externalTextBoxStyles: 'md:mb-7',
        externalContainerStyles: 'flex relative items-center flex-wrap md:col-span-2 xl:col-span-3 md:bg-[url("/images/inbox/background-login-big-navigation-card.png")] md:bg-right-bottom md:bg-no-repeat md:bg-[length:65%]',
    },
];

const cards = [
    {
        key: 'rules',
        image: {
            src: '/images/inbox/woman-holding-checkmark.png',
            alt: 'Woman holding checkmark',
            style: 'w-auto max-h-[100px] max-w-[200px]',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<ClipboardCheckOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Rules
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'As Sixty learns your preferences, we\'ll create rules your messages. Manage those rules to get the most out of your inbox.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.MAIL_RULES),
            text: 'Manage Rules',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
    {
        key: 'smart-folders',
        image: {
            src: '/images/inbox/woman-next-to-folder.png',
            alt: 'Woman next to folder',
            style: 'w-auto max-h-[100px] max-w-[200px]',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<FolderOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Smart Folders
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Optimize Smart Folders to organize and prioritize your inbox, enhance your workflow, and stay on top of emails.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.MONITORED_LABELS),
            text: 'Manage Folders',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
    {
        key: 'known-domains',
        image: {
            src: '/images/inbox/woman-next-to-phone.png',
            altText: 'Woman next to phone',
            style: 'w-auto max-h-[100px] max-w-[200px]',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<GlobeOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Known Domains
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Teach Sixty which email domains are always familiar to you, and should be prioritized accordingly.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.EMAIL_DOMAINS),
            text: 'Manage Known Domains',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
];

function Inbox() {
    return (
        <LandingWithCards mainTitle={mainTitle} bigNavCards={bigNavCards} cards={cards} />
    );
}

export default withAuth(Inbox);
